import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import {
  findExistingCollections,
  listGenerations,
  startGeneration,
} from '@platform/client';
import { subjectSchema } from '@platform/schema/content';
import { MAX_ROWS } from '@platform/schema/generation';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { describeApiError } from '../api-error';
import { PageHeader, SCREEN_GAP } from '../routes/PageHeader';

import { plannerWarning } from './planner-warning';
import { overwrites, previewCsv, slugsOf } from './preview';

import type { PreviewRow } from './preview';
import type { StartGenerationData } from '@platform/client';
import type { ContentStatus } from '@platform/schema/content';

/**
 * Upload a CSV, see what it will do, then queue it — spec 013 §11.
 *
 * Everything here is MUI (§11.1). The one thing worth noticing is what the table shows:
 * not the row as typed, but the **slugs it will produce**, because those decide whether a
 * re-run upserts an existing course or creates a second one. That is the question somebody
 * uploading a corrected file is actually asking.
 */
export function NewGeneration() {
  const navigate = useNavigate();

  // Read before the click, not after. A run with no token succeeds and writes shaped
  // placeholders, which is the failure a reviewer notices last — GEN-20.
  const planner = useQuery({
    queryKey: ['generations', 'planner'],
    queryFn: () => listGenerations({ throwOnError: true }),
  });

  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [fileName, setFileName] = useState<string>();

  const valid = rows.filter((row) => row.row !== undefined);
  const broken = rows.filter((row) => row.error !== undefined);
  const named = valid.filter((row) => row.row?.lessons !== undefined).length;
  const unrecognised = valid.filter(
    (row) => row.topicIgnored !== undefined,
  ).length;
  // A blank topic is guessed as surely as an unrecognised one, so both are confirmed.
  const guessed = valid.filter((row) => row.inferred === true).length;

  // Asked before the click for the same reason: a row upserts by slug, so a slug that is
  // already live changes what queuing does — GEN-106.
  const slugs = slugsOf(valid);
  const existing = useQuery({
    queryKey: ['generations', 'existing', slugs],
    queryFn: () =>
      findExistingCollections({ body: { slugs }, throwOnError: true }),
    enabled: slugs.length > 0 && valid.length <= MAX_ROWS,
  });
  const found = overwrites(valid, existing.data?.data.collections ?? []);

  // Says what is left out, so nobody reads the click as the whole file going in.
  const queueLabel =
    `Queue ${String(valid.length)} module(s)` +
    (broken.length > 0 ? ` — ${String(broken.length)} row(s) skipped` : '');

  const warning =
    planner.data === undefined
      ? undefined
      : plannerWarning(
          planner.data.data.plannerNext,
          planner.data.data.plannerDeclined,
        );

  const read = (file: File | undefined) => {
    if (file === undefined) return;
    setFileName(file.name);
    void file.text().then((text) => {
      const preview = previewCsv(text);
      setRows(preview.rows);
      setError(preview.headerError);
      setNotice(preview.headerNotice);
    });
  };

  const cancel = () => {
    setConfirming(false);
  };

  const submit = () => {
    cancel();
    setBusy(true);
    // `exactOptionalPropertyTypes` is on and the generated body types optional fields as
    // `string` rather than `string | undefined`, so an absent column is dropped rather
    // than sent as undefined.
    const rows = valid.flatMap((parsed) =>
      parsed.row === undefined
        ? []
        : [
            Object.fromEntries(
              Object.entries(parsed.row).filter(
                ([, value]) => value !== undefined,
              ),
            ),
          ],
    ) as NonNullable<StartGenerationData['body']>['rows'];

    const fail = (cause: unknown) => {
      setError(describeApiError(cause));
      setBusy(false);
    };

    void startGeneration({
      body: { rows, ...(fileName === undefined ? {} : { fileName }) },
    })
      .then((result) => {
        // The SDK resolves a failed call rather than throwing, so the typed error
        // arrives here rather than in the catch — GEN-27. Both routes describe it the
        // same way; this one used to raise `no job returned` instead.
        if (result.error !== undefined) {
          fail(result.error);
          return;
        }

        void navigate(`/authoring/jobs/${result.data.id}`);
      })
      .catch(fail);
  };

  return (
    <Stack spacing={SCREEN_GAP}>
      <PageHeader title="Generate a course from a CSV" />

      <Box>
        <Button component="label" variant="outlined">
          Choose a CSV
          <input
            hidden
            type="file"
            accept=".csv,text/csv"
            onChange={(event) => {
              read(event.target.files?.[0]);
            }}
          />
        </Button>
      </Box>

      {error !== undefined && <Alert severity="error">{error}</Alert>}

      {notice !== undefined && <Alert severity="info">{notice}</Alert>}

      {warning !== undefined && <Alert severity="warning">{warning}</Alert>}

      {existing.error !== null && (
        <Alert severity="warning">
          Could not check which modules already exist:{' '}
          {describeApiError(existing.error)}
        </Alert>
      )}

      {found.live.length > 0 && (
        <Alert severity="warning">
          {found.live.length} module(s) already live — queuing adds draft
          lessons to them, live lessons stay as they are.
        </Alert>
      )}

      {found.drafts.length > 0 && (
        <Alert severity="info">
          {found.drafts.length} module(s) already exist as drafts — queuing
          updates them: {found.drafts.join(', ')}.
        </Alert>
      )}

      {found.liveCourses.length > 0 && (
        <Alert severity="info">
          New modules land as drafts in live course(s):{' '}
          {found.liveCourses.join(', ')}. Learners see nothing until you publish
          them.
        </Alert>
      )}

      {broken.length > 0 && (
        <Alert severity="warning">
          {broken.length} row(s) will be skipped — the table says why for each
          one.
        </Alert>
      )}

      {/* A lesson-level file is many lines per module, so the line count is not the job
          size — 1,463 lines of the first partner curriculum are 253 modules. GEN-25. */}
      {named > 0 && (
        <Alert severity="info">
          {named} module(s) name their own lessons, so those titles are written
          as given rather than decided by the planner.
        </Alert>
      )}

      {valid.length > MAX_ROWS && (
        <Alert severity="warning">
          One job takes {MAX_ROWS} modules and this file has {valid.length}.
          Split it, or the queue will refuse the upload.
        </Alert>
      )}

      {unrecognised > 0 && (
        <Alert severity="info">
          {unrecognised} row(s) name a topic that is not one of the{' '}
          {subjectSchema.options.length}, so one will be chosen for them. The
          topics are: {subjectSchema.options.join(', ')}.
        </Alert>
      )}

      {rows.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Line</TableCell>
              <TableCell>Course</TableCell>
              <TableCell>Module</TableCell>
              <TableCell>Lessons</TableCell>
              <TableCell>Topic</TableCell>
              <TableCell>Will write</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.line}>
                <TableCell>{row.line}</TableCell>
                <TableCell>
                  <Given row={row} column="course" />
                </TableCell>
                <TableCell>
                  <Given row={row} column="module" />
                </TableCell>
                <TableCell>
                  {row.row === undefined
                    ? '—'
                    : row.row.lessons === undefined
                      ? 'the planner decides'
                      : row.row.lessons.length}
                </TableCell>
                <TableCell>
                  {row.topic === undefined
                    ? (row.cells?.topic ?? '—')
                    : row.inferred === true
                      ? `${row.topic} (guessed)`
                      : row.topic}
                  <TopicNote row={row} />
                </TableCell>
                <TableCell>
                  {row.error === undefined ? (
                    <WillWrite
                      slug={row.moduleSlug}
                      status={found.statusOf(row.moduleSlug)}
                    />
                  ) : (
                    <>
                      <Chip size="small" color="warning" label="skipped" />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                      >
                        {row.error}
                      </Typography>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Box>
        <Button
          variant="contained"
          disabled={valid.length === 0 || valid.length > MAX_ROWS || busy}
          onClick={() => {
            if (guessed > 0) setConfirming(true);
            else submit();
          }}
        >
          {queueLabel}
        </Button>
      </Box>

      <Dialog open={confirming} onClose={cancel}>
        <DialogTitle>Queue with guessed topics?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {guessed} row(s) will have their topic chosen for them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancel}>Cancel</Button>
          <Button variant="contained" onClick={submit}>
            {queueLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <Typography variant="caption" color="text.secondary">
        Everything is written as a draft. Nothing publishes without you.
        {found.live.length > 0 &&
          ` Lessons already live in ${found.live.join(', ')} are left as they are.`}
      </Typography>
    </Stack>
  );
}

/**
 * A cell as the file held it; an empty one is what an operator has to fix. A line that
 * could not be split has no cells to name, so nothing in it reads as missing.
 */
function Given({
  row,
  column,
}: {
  row: PreviewRow;
  column: 'course' | 'module';
}) {
  const value = row.row?.[column] ?? row.cells?.[column];
  if (value !== undefined) return value;
  if (row.row === undefined && row.cells === undefined) return '—';
  return (
    <Typography variant="body2" component="span" sx={{ color: 'warning.main' }}>
      missing
    </Typography>
  );
}

function TopicNote({ row }: { row: PreviewRow }) {
  const note =
    row.topicCorrected !== undefined
      ? `“${row.topicCorrected}” read as ${row.topic ?? ''}`
      : row.inferred !== true
        ? undefined
        : row.topicIgnored === undefined
          ? 'the planner decides'
          : `“${row.topicIgnored}” is not a topic`;

  if (note === undefined) return null;
  return (
    <Typography variant="caption" color="text.secondary" component="div">
      {note}
    </Typography>
  );
}

/** The module a row writes, or what it lands on when that module is already there. */
function WillWrite({
  slug,
  status,
}: {
  slug: string | undefined;
  status: ContentStatus | undefined;
}) {
  switch (status) {
    case 'PUBLISHED':
      return (
        <Chip
          size="small"
          color="warning"
          label="live module — adds drafts"
          title={slug}
        />
      );
    case 'ARCHIVED':
      return (
        <Chip size="small" label="archived module — adds drafts" title={slug} />
      );
    case 'DRAFT':
      return (
        <Chip
          size="small"
          color="info"
          variant="outlined"
          label="existing draft — updates it"
          title={slug}
        />
      );
    case undefined:
      return <Chip size="small" label={slug} />;
  }
}
